import React, { useCallback, useEffect } from 'react';
import { useCallbackObservable, useObservable } from 'react-use-observable';

import { Popover, Typography } from 'antd';

import type { EHintPath } from 'v2/interfaces/hint';
import { hintFactory } from 'v2/services/hint';

interface IProps {
  inputName: string;
  children: React.ReactNode;
}

export const useHint = (path: EHintPath) => {
  useEffect(() => {
    hintFactory.loadHint(path);
  }, [path]);
};

export const Hint = ({ children, inputName }: IProps) => {
  useEffect(() => {
    hintFactory.setHint(inputName);

    return () => hintFactory.removeHint(inputName);
  }, [inputName]);

  const [hasHint] = useObservable(() => hintFactory.hasHint(inputName), [inputName]);
  const [hint] = useObservable(() => hintFactory.getHint(inputName), [inputName]);
  const [onMouseEnter] = useCallbackObservable(inpName => hintFactory.onMouseEnter(inpName), []);
  const [onMouseLeave] = useCallbackObservable(() => hintFactory.onMouseLeave(), []);

  const handleMouseEnter = useCallback(() => onMouseEnter(inputName), [inputName, onMouseEnter]);

  if (!hint) {
    return <>{children}</>;
  }

  return (
    <Popover
      open={hasHint}
      placement='left'
      title={hint.label}
      content={
        <>
          <div>{hint.hint}</div>
          {!!hint.link && !!hint.link_description && (
            <Typography.Link href={hint.link} rel='noopener noreferrer' target='_blank'>
              {hint.link_description}
            </Typography.Link>
          )}
        </>
      }
    >
      <div onMouseEnter={handleMouseEnter} onMouseLeave={onMouseLeave}>
        {children}
      </div>
    </Popover>
  );
};
